import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { brainiacsService } from '../core/services/brainiacs.service';
import { useAuth } from '../../contexts/AuthContext';
import { queryKeys } from '../../services/query/queryKeys';
import { CREDIT_COST } from '../../utils/creditPricing';
import {
  clearPendingCreditPurchase,
  PAYMENT_CREDITS_BEFORE_KEY,
  PAYMENT_RETURN_URL_KEY,
  setPendingCreditPurchase,
} from '../../utils/paymentCheckout';

interface BuyCreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
}

const CREDIT_PACKAGES = [
  { amount: 25, name: 'Quick start', useCase: 'Light usage' },
  { amount: 100, name: 'Starter', useCase: 'Regular usage' },
  { amount: 500, name: 'Momentum', useCase: 'Active pipeline', recommended: true },
  { amount: 1500, name: 'Growth', useCase: 'Multi-campaign' },
  { amount: 5000, name: 'Scale', useCase: 'High-volume teams' },
];

type CapacityMode = 'enrich' | 'generate';

const COMPACT_LEAD_COUNT_FORMATTER = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

export default function BuyCreditsModal({
  isOpen,
  onClose,
  title = 'Buy More Credits',
  subtitle = 'Choose the balance that fits your current workload. Credits are added immediately after secure Stripe payment.',
}: BuyCreditsModalProps) {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [selectedAmount, setSelectedAmount] = useState<number>(500);
  const [capacityMode, setCapacityMode] = useState<CapacityMode>('generate');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canRender = isOpen && typeof document !== 'undefined';

  useEffect(() => {
    if (!canRender) return;

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [canRender]);

  if (!isOpen || typeof document === 'undefined') return null;

  const selectedPackageIndex = Math.max(
    0,
    CREDIT_PACKAGES.findIndex((pkg) => pkg.amount === selectedAmount)
  );
  const selectedPackage = selectedAmount ? CREDIT_PACKAGES[selectedPackageIndex] : null;
  const sliderProgress = selectedAmount
    ? (selectedPackageIndex / (CREDIT_PACKAGES.length - 1)) * 100
    : 0;
  const generatedLeadEstimate = selectedAmount
    ? Math.floor(selectedAmount / CREDIT_COST.GENERATE)
    : 0;
  const enrichedLeadEstimate = selectedAmount
    ? Math.floor(selectedAmount / CREDIT_COST.ENRICH)
    : 0;
  const selectedLeadEstimate = capacityMode === 'enrich'
    ? enrichedLeadEstimate
    : generatedLeadEstimate;
  const capacityCost = capacityMode === 'enrich'
    ? CREDIT_COST.ENRICH
    : CREDIT_COST.GENERATE;
  const capacityActionLabel = capacityMode === 'enrich' ? 'enriched' : 'generated';

  const selectPackageAmount = (amount: number) => {
    setSelectedAmount(amount);
    setError(null);
  };

  const handleBuyCredits = async () => {
    if (!selectedAmount) {
      setError('Please select an amount');
      return;
    }

    if (!currentUser || !currentUser.email) {
      setError('You must be logged in to purchase credits');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('💳 Purchasing credits:', selectedAmount, 'EUR');

      // Store current path so we can return after payment
      const returnPath = window.location.pathname + window.location.search;
      sessionStorage.setItem(PAYMENT_RETURN_URL_KEY, returnPath);
      const cachedProfile = queryClient.getQueryData<{ credits?: number }>(
        queryKeys.profile.client(currentUser.email)
      );
      const currentCredits = typeof cachedProfile?.credits === 'number' ? cachedProfile.credits : 0;
      sessionStorage.setItem(PAYMENT_CREDITS_BEFORE_KEY, String(currentCredits));
      console.log('💾 Stored return URL:', returnPath);

      const result = await brainiacsService.getStripeCheckoutUrl({
        amount: selectedAmount,
        currency: 'eur',
        successUrl: `${window.location.origin}/billing/success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/billing/cancelled`,
      });

      // API returns data as array: [{url: "..."}]
      const checkoutData = Array.isArray(result.data) ? result.data[0] : result.data;
      const checkoutUrl = checkoutData?.url;

      if (result.success && checkoutUrl) {
        console.log('✅ Stripe checkout URL received:', checkoutUrl);
        setPendingCreditPurchase({
          amount: selectedAmount,
          initiatedAt: new Date().toISOString(),
          returnPath,
          creditsBeforeCheckout: currentCredits,
        });
        // Redirect to Stripe checkout
        window.location.href = checkoutUrl;
      } else {
        throw new Error(result.error || 'Failed to get checkout URL');
      }
    } catch (err: any) {
      console.error('❌ Error purchasing credits:', err);
      clearPendingCreditPurchase();
      setError(err.message || 'Failed to initiate purchase');
      setLoading(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[120] overflow-y-auto bg-black/80 p-3 backdrop-blur-md flex items-center justify-center sm:p-4"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="relative mx-auto w-full max-w-2xl overflow-hidden rounded-[30px] border border-white/8 bg-[radial-gradient(circle_at_top_left,rgba(82,96,255,0.10),transparent_30%),radial-gradient(circle_at_bottom_right,rgba(16,185,129,0.06),transparent_28%),linear-gradient(155deg,#141414,#070707)] shadow-[0_32px_120px_rgba(0,0,0,0.5)] max-h-[calc(100vh-7rem)] overflow-y-auto sm:max-h-[calc(100vh-2rem)]"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(120deg,rgba(255,255,255,0.04),transparent_35%,transparent_65%,rgba(255,255,255,0.03))]" />
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 z-10 rounded-full bg-white/5 p-2 text-white/60 transition-all hover:bg-white/10 hover:text-white sm:right-5 sm:top-5"
          disabled={loading}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Header */}
        <div className="relative px-5 py-5 sm:px-6 sm:py-6">
          <div className="flex items-start gap-3">
            <svg className="mt-0.5 h-9 w-9 shrink-0 text-cyan-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.9" d="M3 4h2l2.1 9.2A2 2 0 009.05 15H18a2 2 0 001.95-1.55L21 8H8" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.9" d="M9 19a1 1 0 100 2 1 1 0 000-2zm9 0a1 1 0 100 2 1 1 0 000-2z" />
            </svg>
            <div className="min-w-0">
              <div className="text-[11px] font-black uppercase tracking-[0.24em] text-cyan-200/70">Credits Checkout</div>
              <h2 className="mt-2 text-2xl font-black tracking-tight text-white sm:text-[2rem]">{title}</h2>
              <p className="mt-2 max-w-xl text-sm leading-6 text-gray-400">
                {subtitle}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="relative p-5 sm:p-6">
          <div className="mb-3 text-[10px] font-bold uppercase tracking-[0.14em] text-white/40">
            Choose a top-up
          </div>

          {/* Credit packages grid */}
          <div className="mb-4 grid grid-cols-2 gap-2.5 sm:grid-cols-5">
            {CREDIT_PACKAGES.map((pkg) => (
              <button
                key={pkg.amount}
                onClick={() => selectPackageAmount(pkg.amount)}
                disabled={loading}
                aria-pressed={selectedAmount === pkg.amount}
                className={`
                  group relative min-h-[132px] overflow-hidden rounded-xl p-3 text-left ring-1 ring-inset transition-all duration-200
                  ${selectedAmount === pkg.amount
                    ? 'bg-cyan-900/25 ring-cyan-300/40 shadow-[0_8px_24px_rgba(82,96,255,0.18)]'
                    : pkg.recommended
                      ? 'bg-cyan-400/[0.055] ring-cyan-300/20 hover:bg-cyan-400/[0.09]'
                      : 'bg-white/[0.035] ring-white/[0.06] hover:bg-white/[0.07] hover:ring-white/[0.12]'
                  }
                  ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                `}
              >
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.06),transparent_45%)] opacity-40 transition-opacity duration-300 group-hover:opacity-70" />
                <div className="relative flex h-full flex-col">
                  <div className="min-h-5">
                    <div className={`whitespace-nowrap text-[8px] font-black uppercase leading-4 tracking-[0.06em] ${pkg.recommended ? 'text-cyan-200/85' : 'text-white/40'}`}>
                      {pkg.recommended ? 'Best balance' : pkg.name}
                    </div>
                  </div>
                  <div className="mt-3 whitespace-nowrap text-[24px] font-black tracking-tight text-white sm:text-[26px]">
                    €{pkg.amount.toLocaleString('en-US')}
                  </div>
                  <div className="mt-auto pr-6 pt-2 text-[10px] font-medium leading-4 text-gray-500">{pkg.useCase}</div>
                </div>
                {selectedAmount === pkg.amount && (
                  <div className="pointer-events-none absolute bottom-3 right-3 flex h-5 w-5 items-center justify-center rounded-full bg-cyan-300 text-[#061116] shadow-[0_0_14px_rgba(103,232,249,0.25)]">
                    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* Discrete workload slider */}
          <div className="mb-5 px-1">
            <div className="flex items-end justify-between gap-4">
              <div>
                <label htmlFor="credit-top-up-range" className="text-[10px] font-black uppercase tracking-[0.16em] text-white/45">
                  Choose by lead volume
                </label>
                <div className="mt-2 inline-flex rounded-lg bg-white/[0.045] p-0.5" role="group" aria-label="Lead action">
                  {(['generate', 'enrich'] as const).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => setCapacityMode(mode)}
                      disabled={loading}
                      aria-pressed={capacityMode === mode}
                      className={`rounded-md px-2.5 py-1 text-[10px] font-bold transition-colors ${
                        capacityMode === mode
                          ? 'bg-white/10 text-white'
                          : 'text-white/35 hover:text-white/60'
                      } disabled:cursor-not-allowed disabled:opacity-50`}
                    >
                      {mode === 'enrich' ? 'Enrich' : 'Generate'}
                    </button>
                  ))}
                </div>
              </div>
              <div className="text-right">
                <div className="text-lg font-black tracking-tight text-white">
                  {selectedAmount ? `Up to ${selectedLeadEstimate.toLocaleString('en-US')}` : 'Move slider'}
                </div>
                <div className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.1em] text-cyan-100/55">
                  Leads {capacityActionLabel}
                </div>
              </div>
            </div>

            <input
              id="credit-top-up-range"
              type="range"
              min="0"
              max={CREDIT_PACKAGES.length - 1}
              step="1"
              value={selectedPackageIndex}
              disabled={loading}
              aria-valuetext={selectedPackage
                ? `Up to ${selectedLeadEstimate.toLocaleString('en-US')} leads ${capacityActionLabel}`
                : 'No top-up selected'}
              onPointerDown={() => {
                if (!selectedAmount) selectPackageAmount(CREDIT_PACKAGES[0].amount);
              }}
              onChange={(event) => {
                const packageIndex = Number(event.target.value);
                selectPackageAmount(CREDIT_PACKAGES[packageIndex].amount);
              }}
              style={{
                background: selectedAmount
                  ? `linear-gradient(to right, rgba(103,232,249,0.88) 0%, rgba(59,130,246,0.78) ${sliderProgress}%, rgba(255,255,255,0.10) ${sliderProgress}%, rgba(255,255,255,0.10) 100%)`
                  : 'rgba(255,255,255,0.10)',
              }}
              className="mt-4 h-1.5 w-full cursor-ew-resize appearance-none rounded-full outline-none disabled:cursor-not-allowed disabled:opacity-50
                [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:appearance-none
                [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-[3px] [&::-webkit-slider-thumb]:border-[#0D1519]
                [&::-webkit-slider-thumb]:bg-cyan-200 [&::-webkit-slider-thumb]:shadow-[0_0_0_3px_rgba(103,232,249,0.16),0_4px_16px_rgba(34,211,238,0.3)]
                [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:hover:scale-110
                [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full
                [&::-moz-range-thumb]:border-[3px] [&::-moz-range-thumb]:border-[#0D1519] [&::-moz-range-thumb]:bg-cyan-200"
            />

            <div className="mt-2 grid grid-cols-5" aria-hidden="true">
              {CREDIT_PACKAGES.map((pkg) => (
                <span
                  key={`range-${pkg.amount}`}
                  className={`text-[9px] font-bold ${
                    selectedAmount === pkg.amount ? 'text-cyan-100' : 'text-white/25'
                  } ${pkg.amount === CREDIT_PACKAGES[0].amount
                    ? 'text-left'
                    : pkg.amount === CREDIT_PACKAGES[CREDIT_PACKAGES.length - 1].amount
                      ? 'text-right'
                      : 'text-center'
                  }`}
                >
                  {COMPACT_LEAD_COUNT_FORMATTER.format(Math.floor(pkg.amount / capacityCost))}
                </span>
              ))}
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-5 rounded-xl border border-red-500/25 bg-red-500/10 p-4">
              <div className="flex items-center gap-2 text-red-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm">{error}</span>
              </div>
            </div>
          )}

          {/* Estimated capacity */}
          <div className="mb-5 rounded-xl bg-white/[0.03] p-4 sm:p-5">
            <div className="min-w-0">
              <div className="text-[11px] font-black uppercase tracking-[0.2em] text-cyan-200/70">Estimated outcome</div>

              <div className="mt-4 grid grid-cols-2 divide-x divide-white/[0.09]">
                <div className="pr-4">
                  <div className="text-[10px] font-bold uppercase tracking-[0.13em] text-cyan-100/55">
                    Leads generated
                  </div>
                  <div className="mt-1 text-[24px] font-black tracking-tight text-white">
                    {selectedAmount ? `Up to ${generatedLeadEstimate.toLocaleString('en-US')}` : '—'}
                  </div>
                  <div className="mt-0.5 text-[10px] text-gray-500">{CREDIT_COST.GENERATE} credits / lead</div>
                </div>
                <div className="pl-4">
                  <div className="text-[10px] font-bold uppercase tracking-[0.13em] text-blue-100/55">
                    Leads enriched
                  </div>
                  <div className="mt-1 text-[24px] font-black tracking-tight text-white">
                    {selectedAmount ? `Up to ${enrichedLeadEstimate.toLocaleString('en-US')}` : '—'}
                  </div>
                  <div className="mt-0.5 text-[10px] text-gray-500">{CREDIT_COST.ENRICH} credits / lead</div>
                </div>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-col gap-3 pt-4 sm:flex-row">
            <button
              onClick={onClose}
              disabled={loading}
              className="rounded-xl bg-white/5 px-6 py-3 font-semibold text-white transition-all hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50 sm:flex-[3]"
            >
              Cancel
            </button>
            <button
              onClick={handleBuyCredits}
              disabled={!selectedAmount || loading}
              className={`
                flex items-center justify-center gap-2 rounded-xl px-6 py-3 font-semibold text-white transition-all sm:flex-[7]
                ${selectedAmount && !loading
                  ? 'bg-[linear-gradient(135deg,#10b981,#0f766e)] shadow-[0_16px_36px_rgba(16,185,129,0.28)] hover:-translate-y-0.5 hover:shadow-[0_20px_44px_rgba(16,185,129,0.34)] cursor-pointer'
                  : 'bg-white/10 opacity-50 cursor-not-allowed'
                }
              `}
            >
              {loading ? (
                <>
                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                  <span>
                    {selectedAmount ? `Continue with €${selectedAmount.toLocaleString('en-US')}` : 'Select Amount'}
                  </span>
                </>
              )}
            </button>
          </div>

          {/* Stripe logo footer */}
          <div className="mt-3 flex items-center justify-end">
            <img
              src="/images/Stripepower.svg"
              alt="Powered by Stripe"
              className="h-6 w-auto opacity-45 transition-opacity hover:opacity-75"
            />
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
